<template>
  <div class="cart-page">
    <div class="container">
      <div v-if="isLoading" class="loading-state">
        <el-skeleton :rows="5" animated />
      </div>

      <div v-else-if="cartItems.length === 0" class="empty-cart">
        <el-empty description="您的購物車還是空的">
          <el-button type="primary" @click="goToShopping">馬上去逛逛</el-button>
        </el-empty>
      </div>

      <div v-else class="cart-layout">
        <!-- 商品列表區域 -->
        <div class="cart-items-section">
          <div class="cart-header">
            <el-checkbox
              v-model="isAllSelected"
              @change="handleSelectAll"
              class="select-all-checkbox"
              :indeterminate="isIndeterminate"
            >
              全選
            </el-checkbox>
            <span>商品信息</span>
            <span>單價</span>
            <span>數量</span>
            <span>小計</span>
            <span>操作</span>
          </div>

          <div v-for="item in cartItems" :key="item.id" class="cart-item">
            <el-checkbox v-model="item.selected" @change="handleItemSelectionChange" class="item-checkbox"></el-checkbox>
            <div class="item-info">
              <img :src="`${backendBaseUrl}/${item.image}`" :alt="item.name" class="item-image" @click="goToProduct(item.productId)" />
              <div class="item-details">
                <h3 class="item-name" @click="goToProduct(item.productId)">{{ item.name }}</h3>
                <p class="item-sku" v-if="item.sku">{{ item.sku }}</p>
              </div>
            </div>
            <div class="item-price">${{ item.price.toLocaleString() }}</div>
            <div class="item-quantity">
              <el-input-number
                v-model="item.quantity"
                :min="1"
                :max="item.stock || 99"
                size="small"
                controls-position="right"
                @change="(currentValue, oldValue) => handleQuantityChange(item, currentValue, oldValue)"
              />
            </div>
            <div class="item-subtotal">${{ (item.price * item.quantity).toLocaleString() }}</div>
            <div class="item-actions">
              <el-button text type="danger" size="small" @click="removeItem(item.id)">
                <el-icon><Delete /></el-icon> 刪除
              </el-button>
            </div>
          </div>
          <div class="cart-footer-actions">
            <el-button type="danger" plain @click="removeSelectedItems" :disabled="selectedItems.length === 0">
                刪除選中商品
            </el-button>
          </div>
        </div>

        <!-- 訂單摘要區域 -->
        <div class="order-summary-section">
          <div class="summary-card">
            <h2 class="summary-title">訂單摘要</h2>
            <div class="summary-item">
              <span>選擇商品總數:</span>
              <span>{{ totalSelectedItemsQuantity }} 件</span>
            </div>
            <div class="summary-item">
              <span>商品總金額:</span>
              <span>${{ selectedItemsTotalPrice.toLocaleString() }}</span>
            </div>
            <div class="summary-item">
              <span>運費:</span>
              <span>{{ shippingFee > 0 ? '$' + shippingFee.toLocaleString() : '免運費' }}</span>
            </div>
            <div class="summary-item coupon-item" v-if="appliedCoupon">
              <span>優惠券 ({{ appliedCoupon.name }}):</span>
              <span class="discount-amount"> {{ appliedCoupon.discountValue.toLocaleString() * 100 }}% off</span>
            </div>
            <el-divider />
            <div class="summary-total">
              <span>應付總額:</span>
              <span class="total-amount">${{ finalAmount.toLocaleString() }}</span>
            </div>
            <el-button
              type="primary"
              class="checkout-button"
              @click="goToCheckout"
              :disabled="selectedItems.length === 0"
            >
              去結算 ({{ totalSelectedItemsQuantity }})
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete } from '@element-plus/icons-vue'; // 引入图标
import cartService from '../../services/ShoppingCar';
import discountServer from '../../services/discountManage';

const router = useRouter();

const isLoading = ref(true);
const cartItems = ref([]);
const shippingFee = ref(0); // 模拟运费，可以根据商品总额、地区等计算
const appliedCoupon = ref(null); // 模拟应用的优惠券 { name: '新人券', discountValue: 20 }
const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL;

const fetchCartItems = async () => {
  isLoading.value = true;
  try {
    const response = await cartService.getShoppingCar();
    cartItems.value = response
  } catch (error) {
    ElMessage.error('加载购物车失败，请稍后重试');
    console.error("Error fetching cart items:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  const result = await discountServer.getCurrentCampaign();
  if (Object.keys(result).length > 0) {
    appliedCoupon.value = result;
  }
  fetchCartItems();
});

// --- 选择逻辑 ---
const isAllSelected = computed({
  get: () => cartItems.value.length > 0 && cartItems.value.every(item => item.selected),
  set: (value) => {
    cartItems.value.forEach(item => item.selected = value);
  }
});

const isIndeterminate = computed(() => {
  const selectedCount = cartItems.value.filter(item => item.selected).length;
  return selectedCount > 0 && selectedCount < cartItems.value.length;
});

const handleSelectAll = (value) => {
  cartItems.value.forEach(item => item.selected = value);
};

const handleItemSelectionChange = () => {
  // 这个函数主要用于触发 isAllSelected 和 isIndeterminate 的重新计算
  // 实际的 item.selected 已经是 v-model 双向绑定的结果
};

const debounce = (fn, delay) => {
  let timer = null; // 存储定时器和开始时间
  
  return (...args) => {
    const now = Date.now();
    
    // 若定时器已存在，检查时间差
    if (timer) {
      const timeDiff = now - timer.startTime;
      
      // 若距离上次操作超过 delay，则立即执行
      if (timeDiff > delay) {
        clearTimeout(timer.id); // 清除旧定时器
        fn.apply(this, args);   // 执行函数
        timer = null;           // 重置定时器
      } 
      // 否则，计算剩余时间并设置新定时器
      else {
        clearTimeout(timer.id);
        timer.id = setTimeout(() => {
          fn.apply(this, args);
          timer = null;
        }, delay - timeDiff);
      }
    } 
    // 若定时器不存在，创建新的定时器
    else {
      timer = {
        startTime: now,
        id: setTimeout(() => {
          fn.apply(this, args);
          timer = null;
        }, delay)
      };
    }
  };
};

// --- 数量变更 ---
const handleQuantityChange = debounce(async (item, currentValue, oldValue) => {
  // 实际项目中，这里应该调用 API 更新服务器上的购物车商品数量
  await cartService.updateItemQuantity(item.id, currentValue);
  ElMessage.success(`商品 "${item.id}" 数量已更新为 ${currentValue}`);
  // 如果更新失败，需要将 item.quantity 恢复为 oldValue
},2000);

// --- 删除商品 ---
const removeItem = async (itemId) => {
  try {
    await ElMessageBox.confirm('确定要从购物车中移除该商品吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    });
    await cartService.deleteShoppingCar(itemId);
    ElMessage.success('商品已成功移除');
    fetchCartItems();
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('移除商品失败');
      console.error("Error removing item:", error);
    }
  }
};

const removeSelectedItems = async () => {
    if (selectedItems.value.length === 0) {
        ElMessage.warning('请至少选择一个商品进行删除');
        return;
    }
    try {
        await ElMessageBox.confirm(`确定要从购物车中移除选中的 ${selectedItems.value.length} 件商品吗？`, '批量删除确认', {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning',
        });
        const itemIdsToRemove = selectedItems.value.map(item => item.id);
        await cartService.deleteShoppingCar(itemIdsToRemove); // 假设有这样的API
        fetchCartItems();
        ElMessage.success('选中的商品已成功移除');
    } catch (error) {
        if (error !== 'cancel') {
            ElMessage.error('批量移除商品失败');
            console.error("Error removing selected items:", error);
        }
    }
};


// --- 计算摘要 ---
const selectedItems = computed(() => cartItems.value.filter(item => item.selected));

const totalSelectedItemsQuantity = computed(() => {
  return selectedItems.value.reduce((sum, item) => sum + item.quantity, 0);
});

const selectedItemsTotalPrice = computed(() => {
  return selectedItems.value.reduce((sum, item) => sum + item.price * item.quantity, 0);
});



const finalAmount = computed(() => {
  let amount = selectedItemsTotalPrice.value + shippingFee.value;
  if (appliedCoupon.value) {
    amount -= amount * appliedCoupon.value.discountValue;
  }
  return Math.max(0, amount); // 确保总额不为负
});

// --- 导航 ---
const goToShopping = () => {
  router.push('/shoes'); // 跳转到商品列表页或首页
};

const goToProduct = (productId) => {
  router.push({ name: 'ShoeDetail', params: { id: productId } });
};

const goToCheckout = () => {
  if (selectedItems.value.length === 0) {
    ElMessage.warning('请至少选择一件商品进行结算');
    return;
  }
  const itemsToCheckout = selectedItems.value.map(item => ({
    id: item.id,                 // 購物車項目的 ID (用於後端清空購物車等)
    productId: item.productId,   // 商品本身的 ID
    name: item.name,
    image: item.image,           // 圖片路徑 (相對於 backendBaseUrl)
    sku: item.sku,               // 商品 SKU (如果有的話)
    price: item.price,           // 商品單價 (下單時的價格)
    normal_price: item.normal_price,
    quantity: item.quantity,     // 商品數量
  }));

  // 計算價格
  const subtotalOriginal = selectedItemsTotalPrice.value;

  const normalSubtotalOriginal = selectedItems.value.reduce(
    (sum, item) => sum + (item.normal_price || item.price) * item.quantity, 
    0
  );


  // 3. 準備應用的折扣信息
  let appliedDiscountInfo = null;
  let deductedAmount = 0;

  if (appliedCoupon.value && appliedCoupon.value.discountValue > 0 && subtotalOriginal > 0) {
    // 這裡假設 appliedCoupon.value.discountValue 是折扣率, e.g., 0.1 for 10% off
    // 如果你的 appliedCoupon.value.discountValue 是固定金額，則直接使用它
    // 你需要根據你的 discountValue 的實際含義來計算 deductedAmount
    // 例如，如果 discountValue 是 0.2 (代表八折或20% off)
    deductedAmount = subtotalOriginal * appliedCoupon.value.discountValue;

    appliedDiscountInfo = {
      name: appliedCoupon.value.name, // 優惠活動名稱
      rate: appliedCoupon.value.discountValue, // 折扣率 (可選，如果確認頁要顯示)
      deductedAmount: parseFloat(deductedAmount.toFixed(2)) // 實際扣減的金額
    };
  }

  const finalAmountForNormal = normalSubtotalOriginal + shippingFee.value;

  const checkoutDataForConfirm = {
    itemsToCheckout: itemsToCheckout,
    normal_subtotalOriginal: parseFloat(normalSubtotalOriginal.toFixed(2)),
    normal_totalAmount: parseFloat(finalAmountForNormal.toFixed(2)),
    subtotalOriginal: parseFloat(subtotalOriginal.toFixed(2)),
    appliedDiscountInfo: appliedDiscountInfo, // 如果沒有折扣，此值為 null
    shippingFee: shippingFee.value,
    totalAmount: parseFloat(finalAmount.value.toFixed(2)),
    notes: '', // 購物車頁面通常沒有全局訂單備註，確認頁可以填寫
    // selectedAddressId: null, // 地址選擇在 ConfirmOrderPage 或其子流程處理
  };

  // 实际项目中，这里会将选中的商品信息传递给结算页面
  // router.push({ name: 'CheckoutPage', state: { itemsToCheckout: selectedItems.value } });
  ElMessage.success(`准备结算 ${totalSelectedItemsQuantity.value} 件商品，总计 $${finalAmount.value.toLocaleString()}`);
  router.push({
    name: 'CheckOrder', // 這是你在路由中定義的 ConfirmOrderPage 的 name
    state: { checkoutDataForConfirm: checkoutDataForConfirm }
  });
};

</script>

<style scoped lang="scss">
.cart-page {
  min-height: calc(100vh - 72px); /* 假设头部72px */
  padding: 2rem 0;
}

.container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 1rem;
}

.loading-state,
.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  padding: 2rem;
}
.empty-cart .el-button {
  margin-top: 1.5rem;
}

.cart-layout {
  display: flex;
  flex-wrap: wrap; /* 允许在小屏幕上换行 */
  gap: 1.5rem; /* 主区域和侧边栏之间的间距 */
}

.cart-items-section {
  flex-grow: 4; /* 或者更大的值，例如 5。原先是 3 */
  flex-shrink: 1; /* 允许在空间不足时收缩 */
  flex-basis: 0%;  /* 从0开始分配空间，让 flex-grow 完全主导 */
  min-width: 0; /* 防止内容溢出导致 flex item 不按比例分配 */
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
}

.cart-header {
  display: grid;
  grid-template-columns: auto 1fr auto auto auto auto; /* 调整列定义以适应复选框 */
  align-items: center;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e9ebf0;
  font-weight: 500;
  color: #5a657c;
  font-size: 0.9rem;
  gap: 1rem; /* 列间距 */

  span:nth-child(2) { text-align: left; } /* 商品信息左对齐 */
  span:not(:nth-child(2)) { text-align: center; } /* 其他居中 */
}
.select-all-checkbox {
    margin-right: 0.5rem;
}


.cart-item {
  display: grid;
  grid-template-columns: auto 1fr auto auto auto auto; /* 同上 */
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f2f3f5; /* 更浅的商品间分隔线 */
  gap: 1rem;

  &:last-child {
    border-bottom: none;
  }
}
.item-checkbox {
    margin-right: 0.5rem;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
}

.item-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e9ebf0;
}

.item-details {
  text-align: left;
}

.item-name {
  font-size: 1rem;
  font-weight: 500;
  color: #1a2b48;
  margin: 0 0 0.25rem 0;
  line-height: 1.4;
  // 文字超出两行显示省略号
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-sku {
  font-size: 0.8rem;
  color: #778299;
}

.item-price,
.item-subtotal {
  font-size: 0.95rem;
  color: #1a2b48;
  font-weight: 500;
  text-align: center;
}
.item-quantity {
    text-align: center;
    .el-input-number--small {
        width: 100px; // 调整数量选择器宽度
    }
}


.item-actions {
  text-align: center;
  .el-button--text { // 使文字按钮更紧凑
    padding: 4px 8px;
  }
}

.cart-footer-actions {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ebf0;
    display: flex;
    justify-content: flex-end;
}


.order-summary-section {
  flex: 1;
  min-width: 280px; /* 摘要区域最小宽度 */
}

.summary-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  position: sticky; /* 当页面滚动时，摘要区域可以固定 */
  top: 92px; /* (header高度 + 一些间距) */
}

.summary-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a2b48;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ebf0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #3c4962;
  margin-bottom: 0.75rem;

  &.coupon-item span:last-child {
    color: #e53e3e; /* 折扣金额用红色 */
    font-weight: 500;
  }
}

.el-divider {
  margin: 1rem 0 1.25rem;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a2b48;
  margin-bottom: 1.5rem;

  .total-amount {
    color: #e53e3e; /* 总金额用醒目的红色 */
    font-size: 1.25rem;
  }
}

.checkout-button {
  width: 100%;
  height: 44px;
  font-size: 1rem;
}

// Responsive adjustments
@media (max-width: 992px) { // Tablet
  .cart-layout {
    flex-direction: column; /* 垂直堆叠 */
  }
  .order-summary-section {
    width: 100%;
    position: static; /* 取消固定定位 */
    margin-top: 0; /* 如果之前有top，重置 */
  }
  .cart-header {
    // 在平板上可以考虑隐藏部分列，或使其更紧凑
    grid-template-columns: auto 1fr auto auto auto; /* 移除操作列 */
    span:last-child { display: none; } /* 隐藏操作标题 */
  }
  .cart-item {
    grid-template-columns: auto 1fr auto auto auto; /* 移除操作列 */
    .item-actions { display: none; } /* 隐藏操作按钮，可以在商品信息中添加删除图标 */
    // 或者将操作按钮移到商品信息下方
  }
  // 为了在平板上更好显示，可以考虑在 item-details 中添加删除按钮
  .item-details {
      position: relative;
  }
  /* 如果想在商品详情里放删除按钮，可以取消下面注释
  .item-details::after {
      content: '删除'; // 或者用图标
      position: absolute;
      right: 0;
      top: 0; // 或者 bottom:0;
      color: #e53e3e;
      cursor: pointer;
      font-size: 0.8rem;
  }
  */
}

@media (max-width: 768px) { // Mobile
  .page-title {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
  }
  .cart-items-section, .summary-card {
    padding: 1rem;
  }
  .cart-header {
    font-size: 0.8rem;
    padding-bottom: 0.75rem;
    margin-bottom: 0.75rem;
    grid-template-columns: auto 1fr auto; /* 进一步简化：复选框、商品、小计 */
    span:nth-child(4), span:nth-child(5), span:nth-child(6) { display: none; } /* 隐藏单价、数量、操作标题 */
  }
  .cart-item {
    grid-template-columns: auto 1fr auto; /* 复选框、商品、小计 */
    gap: 0.75rem;
    padding: 0.75rem 0;
     .item-price, .item-quantity, .item-actions { display: none; } // 隐藏单价、数量和独立的操作列
  }
  // 在移动端，数量调整和删除操作可以集成到 item-details 中
  .item-image {
    width: 60px;
    height: 60px;
  }
  .item-name {
    font-size: 0.9rem;
  }
  .item-sku {
      font-size: 0.75rem;
  }
  .item-subtotal {
      font-size: 0.9rem;
  }

  // 如果要在商品详情下放数量和删除：
  .item-details {
      display: flex;
      flex-direction: column;
  }
  /*
  .mobile-item-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 0.5rem;
  }
  .mobile-item-controls .el-input-number--small {
      width: 90px;
  }
  */

  .summary-title { font-size: 1.1rem; }
  .summary-item, .summary-total { font-size: 0.85rem; }
  .summary-total .total-amount { font-size: 1.1rem; }
  .checkout-button { height: 40px; font-size: 0.95rem; }
}

@media (max-width: 480px) {
    .cart-header, .cart-item {
        // 在极小屏幕上，可以考虑进一步简化，或者让商品信息垂直排列
        grid-template-columns: auto 1fr; // 只保留复选框和商品信息（此时小计也隐藏了）
        span:nth-child(3), .item-subtotal { display: none; }
    }
    .item-info {
        gap: 0.75rem;
    }
    .item-image {
        width: 50px;
        height: 50px;
    }
    .item-name {
        font-size: 0.85rem;
    }
}

</style>